/**
 * How a snap's log reads in the text box (slice 8): every line of `playLogEntries`, in order, as
 * its event plays. By default (slice 8c) the play runs as one motion: every line goes by at
 * reading speed and nothing waits for a tap, the result line included. With the Dev tab's "pause
 * on key moments" the key lines hold the play until a tap: a crit, a super-effective hit, a
 * turnover, and the result line ("pause on every battle" adds every battle). Which lines are key
 * is read off the sim's events; nothing here decides anything about the play.
 */
import type { LogEntry, PlayEvent } from '@tdmons/sim';
import type { PlayView } from '../field/playView';

export const SUPER_EFFECTIVE = "It's super effective!";

/** A line for the text box. */
export interface BoxLine {
  text: string;
  numbers?: string;
  /** Shown in the red-tinted super-effective color. */
  superEffective?: boolean;
  /** Waits for a tap; otherwise it goes by at reading speed. */
  hold: boolean;
}

export interface SnapLine extends BoxLine {
  entry: LogEntry;
  /** The sim time of the event the line describes. */
  t: number;
  /** The result line, at the whistle. */
  whistle: boolean;
}

/** The Dev tab's pause toggles; both off by default. "Every battle" implies key moments. */
export interface Pauses {
  keyMoments: boolean;
  everyBattle: boolean;
}

export const NO_PAUSES: Pauses = { keyMoments: false, everyBattle: false };

/** Whether any line can hold the play. */
export const pausing = (p: Pauses): boolean => p.keyMoments || p.everyBattle;

/** A key line: the player must tap past it when pausing on key moments. */
export function isKeyLine(e: PlayEvent | undefined, everyBattle: boolean): boolean {
  switch (e?.type) {
    case 'battle':
      return everyBattle || e.matchup.crit !== undefined || e.matchup.superEffective;
    case 'interception':
    case 'fumble':
    case 'whistle':
      return true;
    default:
      return false;
  }
}

/** The snap's lines in order, each with its event's time and whether it holds. */
export function snapLines(view: PlayView, pauses: Pauses): SnapLine[] {
  return view.entries.map((entry) => {
    const e = view.events[entry.event];
    return {
      entry,
      t: e?.t ?? 0,
      whistle: e?.type === 'whistle',
      text: entry.text,
      ...(entry.numbers !== undefined ? { numbers: entry.numbers } : {}),
      superEffective: entry.line.includes(SUPER_EFFECTIVE),
      hold: pausing(pauses) && isKeyLine(e, pauses.everyBattle),
    };
  });
}

/**
 * The sim times the field stops at: each held line's event, but not the whistle (the clock stops
 * there anyway). One entry per held line, so two key lines at one time need two taps.
 */
export function holdTimes(lines: readonly SnapLine[]): number[] {
  return lines.filter((l) => l.hold && !l.whistle).map((l) => l.t);
}
